import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { CssBaseline, LinearProgress, ThemeProvider } from '@mui/material'
import Grid from '@mui/material/Grid'
import saveAs from 'file-saver'
import _ from 'lodash'
import { useSnackbar } from 'notistack'

import { useGetContactQuery, useGetCustomerQuery } from '@/app/services/customer'
import { useCreateStripeCheckoutSessionMutation } from '@/app/services/integrations'
import { useGetOrderQuery, useLazyGetIssuesQuery } from '@/app/services/orders'
import { useLazyGetTaxRatesQuery } from '@/app/services/taxRates'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import DocumentsToolbar from '@/common/components/AppBar/DocumentsToolbar'
import { extractCSSFromDOM } from '@/common/components/PDFExporter/PDFExporterHelpers'
import { useOnAllImagesLoaded } from '@/common/hooks/useOnAllImagesLoaded'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import PdfService from '@/common/services/PdfService'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { PrintDocumentsOverrides } from '@/common/themes/overrides/PrintDocumentsOverrides'
import { StoreThemeOverride } from '@/common/themes/overrides/StoreThemeOverride'
import { Constants, Paths } from '@/common/utils'

import PublicDocuments from './PublicDocuments'

const classes = {
    loadingMessage: {
        fontFamily: 'fontFamily',
        fontSize: 'body2.fontSize',
        maxWidth: 500,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        textAlign: 'center',
        margin: '48px auto',
    },
}

const Documents = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { showShipping } = useToolBoxTreatments()
    const { action, documentType, orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const organisation = useSelector(selectOrganisation)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)

    const imageWrapperRef = useRef(null)
    const { isImageLoading } = useOnAllImagesLoaded(imageWrapperRef)

    const [isInitiatingPayment, setIsInitiatingPayment] = useState(false)

    const [taxRate, setTaxRate] = useState(null)

    const [isExporting, setIsExporting] = useState(false)
    const [isAutoExportDone, setIsAutoExportDone] = useState(false)
    const [issueSeverityDictionary, setIssueSeverityDictionary] = useState(null)

    const {
        data: order,
        isLoading,
        isSuccess,
    } = useGetOrderQuery({
        organisationId,
        orderId,
        params: {
            includeItems: true,
        },
    })

    const { data: currentCustomer } = useGetCustomerQuery({
        organisationId,
        customerId: currentCustomerId || order?.customerContact.customerId,
    })
    const { data: currentContact } = useGetContactQuery({
        organisationId,
        customerId: currentCustomerId || order?.customerContact.customerId,
        contactId: currentContactId || order?.customerContactId,
    })

    const [getIssues] = useLazyGetIssuesQuery()
    const [getTaxRates] = useLazyGetTaxRatesQuery()
    const [createStripeCheckoutSession] = useCreateStripeCheckoutSessionMutation()

    const theme = useMemo(
        () => PrintDocumentsOverrides(StoreThemeOverride ? StoreThemeOverride(lightTheme, organisation) : lightTheme),
        [StoreThemeOverride, lightTheme, organisation]
    )

    const exportPdf = useCallback(async () => {
        if (documentType === 'work-order' || documentType === 'production-labels') {
            window.print()

            return
        }
        setIsExporting(true)
        const content = document.getElementById(Constants.EXPORTABLE_CONTENT)
        const htmlBody = content.outerHTML
        const cssContent = extractCSSFromDOM()

        try {
            const data = await PdfService.getPdf(order?.name, `<style>${cssContent}</style>`, htmlBody)
            saveAs(data, `${order?.name}-${documentType}.pdf`, { autoBom: true })
        } finally {
            setIsExporting(false)
        }
    }, [order])

    // const handleGetIssues = async () => {
    //     const issues = await getIssues({ orderId }).unwrap()
    //     setIssueSeverityDictionary(issues)
    // }

    const handlePaymentButton = useCallback(async () => {
        setIsInitiatingPayment(true)
        try {
            const stripeCheckoutSessionUrl = await createStripeCheckoutSession({
                organisationId: order.organisationId,
                orderId: orderId,
                successUrl: `${import.meta.env.VITE_AUTH_REDIRECT_URI}/payments/${orderId}/success`,
                cancelUrl: `${import.meta.env.VITE_AUTH_REDIRECT_URI}/documents/${orderId}/${documentType}`,
            }).unwrap()

            enqueueSnackbar(t('Redirecting to Stripe...'), {
                variant: 'info',
                onExited: () => {
                    window.open(stripeCheckoutSessionUrl, '_self')
                    setIsInitiatingPayment(false)
                },
            })
        } catch (error) {
            console.error(t('Payment Button Error'), error)
            enqueueSnackbar(error.data || t('Error initiating payment'), {
                variant: 'error',
            })
        } finally {
            setIsInitiatingPayment(false)
        }
    }, [createStripeCheckoutSession, documentType, enqueueSnackbar, order, orderId])

    useEffect(() => {
        const documentTypes = Object.values(Constants.PUBLIC_DOCUMENTS)

        if (!documentTypes.includes(documentType)) {
            navigate(Paths.NOT_FOUND)
        }
    }, [documentType])

    useEffect(() => {
        const fetchAsync = async () => {
            if (order?.taxRateId) {
                try {
                    const taxRatesResponse = await getTaxRates({
                        organisationId: order.organisationId,
                        params: { includeDeleted: true },
                    }).unwrap()

                    const taxRateFound = taxRatesResponse.find((taxRate) => taxRate.id === order.taxRateId)
                    setTaxRate(taxRateFound)
                } catch (error) {
                    console.error('Documents', 'Tax Rate Error', error)
                }
            }
        }

        fetchAsync()
    }, [orderId, navigate, organisationId])

    // useEffect(() => {
    //     if (!issueSeverityDictionary) {
    //         handleGetIssues()
    //     }
    // }, [issueSeverityDictionary])

    useEffect(() => {
        if (action === 'export' && order && !isLoading && !isImageLoading && !isAutoExportDone) {
            exportPdf(true)
            setIsAutoExportDone(true)
        }
    }, [action, order, exportPdf, isLoading, isImageLoading, isAutoExportDone])

    return !isLoading && !_.isEmpty(order) && organisation ? (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <DocumentsToolbar
                buttonLabel={isImageLoading ? 'Loading document...' : 'Export PDF'}
                disabled={isImageLoading}
                handleBackButton={() => navigate(Paths.ORDER_HISTORY_PATHNAME)}
                handleExportButton={exportPdf}
                handlePaymentButton={handlePaymentButton}
                isExporting={isExporting}
                isInitiatingPayment={isInitiatingPayment}
                orderStatus={order?.status}
                paymentsEnabled={organisation.paymentsEnabledInternal}
                paymentStatus={order?.paymentStatus}
            />
            <Grid
                id="image-wrapper"
                ref={imageWrapperRef}
                size={12}
            >
                <PublicDocuments
                    customer={currentCustomer}
                    customerContact={currentContact}
                    documentType={documentType}
                    issueSeverityDictionary={issueSeverityDictionary}
                    order={order}
                    organisation={organisation}
                    paymentsEnabled={organisation.paymentsEnabledInternal}
                    showShipping={showShipping}
                    taxRate={taxRate}
                />
            </Grid>
        </ThemeProvider>
    ) : (
        <Grid
            size={12}
            sx={classes.loadingMessage}
        >
            {t('We are loading your document. Please, wait.')}
            <LinearProgress style={{ marginTop: 50 }} />
        </Grid>
    )
}

export default Documents
